import { zodResolver } from "@hookform/resolvers/zod";
import { ActionIcon, Button, Card, Checkbox, Group, LoadingOverlay, Stack, Tooltip } from "@mantine/core";
import { CaretLeftIcon, CheckFatIcon, PencilIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";

import { Input as MInput } from "@/components/@custom/Input";
import { useSession } from "@/hooks/@custom/useSession";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { GLOBAL_SCHEMA } from "@/lib/zod";
import api from "@/services/api";
import { useSaveTermsAuthorizationMutation, useSaveUnbindTruckMutation } from "@/store/apis/nextcargo";
import { useGetDriverQuery } from "@/store/apis/nextcargo/driver";

type Schema = z.infer<typeof schema>;
const schema = z.object({
  user: z.object({
    name: GLOBAL_SCHEMA.NAME,
    cpf: GLOBAL_SCHEMA.CPF,
    birthDate: GLOBAL_SCHEMA.BIRTHDAY,
    cnh: GLOBAL_SCHEMA.CNH
  }),
  terms: GLOBAL_SCHEMA.OPTIONAL.TERMS,
  shippingId: z.number().optional(),
  contacts: z.object({
    active: GLOBAL_SCHEMA.OPTIONAL.CONTACT,
    data: z.array(
      z.object({
        id: z.number().optional().nullable(),
        email: z.string().optional().nullable(),
        fone: z.string().optional().nullable(),
        whatsapp: z.string().optional().nullable()
      })
    )
  })
});

export const Route = createFileRoute("/app/perfil/editar")({
  component: DriverProfile
});

function DriverProfile() {
  const navigate = useNavigate();
  const { driver } = useSession();

  const shippingCompanyId = localStorage.getItem("@App:shippingCompanyId");
  const companyId = driver?.company.id;
  const driverId = driver?.id;

  const {
    data: driverData,
    isFetching,
    isLoading,
    isError,
    isSuccess
  } = useGetDriverQuery(
    { id: driverId! },
    {
      skip: !driverId,
      selectFromResult: (state) => {
        const { data } = state;
        const authorization = data?.authorization[0];
        const $acceptedContacts: ("email" | "sms" | "whats")[] = [];
        const keys = ["hasEmail", "hasSMS", "hasWhats"] as const;
        const keyMap = { hasEmail: "email", hasSMS: "sms", hasWhats: "whats" } as const;

        keys.forEach((key) => {
          if (authorization?.[key]) {
            $acceptedContacts.push(keyMap[key]);
          }
        });

        return {
          ...state,
          data: data ? { ...data, $acceptedContacts } : undefined
        };
      }
    }
  );
  // const [saveDeleteTruck] = useSaveDeleteTruckMutation();
  const [saveUnbindTruck] = useSaveUnbindTruckMutation();
  const [saveTermsAuthorization] = useSaveTermsAuthorizationMutation();

  console.log(driverData);
  const authorization = driverData?.authorization[0];
  const authorizationId = authorization?.id;
  const truck = driverData?.truck || [];

  const {
    control,
    getValues,
    reset,
    formState: { isValid, isSubmitting },
    handleSubmit: handleSubmitForm
  } = useForm<Schema>({
    mode: "all",
    resolver: zodResolver(schema),
    defaultValues: {
      user: {
        name: "",
        cpf: "",
        birthDate: "",
        cnh: ""
      },
      shippingId: 0,
      terms: {
        id: 0,
        updatedAt: ""
      },
      contacts: {
        active: [],
        data: [
          {
            id: 0,
            email: "",
            fone: "",
            whatsapp: ""
          }
        ]
      }
    }
  });

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const {
      user,
      terms,
      shippingId,
      contacts: { active: activeContacts, data: contactData }
    } = data;

    const contactBody = {
      id: authorizationId,
      hasWhats: activeContacts.includes("whats"),
      hasSMS: activeContacts.includes("sms"),
      hasEmail: activeContacts.includes("email"),
      driver: {
        id: Number(driverId)
      },
      useTerm: terms
    };

    const userBody = {
      id: parseInt(`${driverId}`), // OK,
      name: user.name.toUpperCase(), // OK
      cpf: user.cpf, // OK
      cnh: user.cnh, // OK
      birthDate: user.birthDate, // OK
      shippingCompany:
        shippingCompanyId || shippingId
          ? {
              id: parseInt(shippingCompanyId || "") || shippingId || null
            }
          : null,
      contact: contactData,
      company: { id: companyId || null }
    };

    try {
      const promises = [api.post("/driver/save", userBody), saveTermsAuthorization(contactBody)];
      await Promise.all(promises);

      toast.success("Perfil editado com sucesso");
      // setTimeout(() => {
      //   navigate({ to: "/app", replace: true });
      // }, 2000);
    } catch {
      // TODO: Melhorar tratamento de erros
      /* if (err.response.data.errno === 1062) {
        toast.error("Erro ao cadastrar, estes dados já existem");
      } else {
        toast.error(`Erro ao Cadastrar: ${err.response.data.code}`);
      } */
    }
  };

  async function handleUnbindTruck(id: number) {
    await saveUnbindTruck({ truck: { id } });
  }

  function handleConfirmDelete() {
    if (!isValid) {
      toast.error("Preencha os campos corretamente antes de excluir sua conta");
      return;
    }

    const cpf = getValues("user.cpf");
    const birthDate = getValues("user.birthDate");
    MODAL_HANDLER.driver.delete({
      birthDate,
      cpf,
      onSuccess() {
        navigate({ to: "/app", replace: true });
      },
      onError(error) {
        toast.error(`Erro ao excluir conta: ${error}`);
      }
    });
  }

  function handleNavigateToEditTruck(id: number) {
    navigate({
      to: "/app/caminhao/editar/$id",
      params: { id }
    });
  }

  useEffect(() => {
    if (!isLoading && isSuccess) {
      const authorization = driverData?.authorization[0];

      reset({
        contacts: {
          active: driverData?.$acceptedContacts || [],
          data: [
            {
              id: driverData?.contact[0]?.id,
              email: driverData?.contact[0]?.email,
              fone: driverData?.contact[0]?.fone,
              whatsapp: driverData?.contact[0]?.whatsapp
            }
          ]
        },
        shippingId: driverData?.shippingCompany?.id,
        terms: {
          id: authorization?.useTerm?.id,
          updatedAt: authorization?.useTerm?.updatedAt as string
        },
        user: {
          name: driverData?.name,
          cpf: driverData?.cpf,
          birthDate: driverData?.birthDate,
          cnh: driverData?.cnh
        }
      });
    }
    // trigger();
  }, [isLoading, isSuccess]);

  return (
    <form onSubmit={handleSubmitForm(onSubmit)} autoComplete="off">
      <Card component={Stack} p="xl" radius="lg" className="relative!" shadow="md" bg="white" withBorder>
        <header className="relative flex items-center justify-center gap-2 rounded">
          <ActionIcon component={Link} to="/app" color="primary" radius="sm" variant="filled" className="absolute! left-0">
            <CaretLeftIcon weight="bold" />
          </ActionIcon>

          <p className="text-primary self-center text-2xl font-semibold">Editar Perfil</p>
        </header>

        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <MInput.Text control={control} label="Nome completo" placeholder="Nome completo" name="user.name" />

            <MInput.Text control={control} label="CPF" placeholder="CPF" name="user.cpf" maskProps={{ mask: "999.999.999-99" }} />

            <MInput.Text control={control} label="Data de nascimento" placeholder="Data de nascimento" name="user.birthDate" maskProps={{ mask: "99/99/9999" }} />

            <MInput.Text control={control} label="CNH" placeholder="CNH" name="user.cnh" maskProps={{ mask: "99999999999" }} />
          </div>

          <div className="flex flex-col gap-2">
            <h2 className="text-primary text-xl font-bold">Caminhões</h2>

            <div className="overflow-x-auto rounded shadow">
              <table className="min-w-full bg-white">
                <thead>
                  <tr>
                    <th className="bg-gray-100 px-4 py-2 font-semibold text-gray-700">Placa</th>
                    <th className="bg-gray-100 px-4 py-2 font-semibold text-gray-700">Renavam</th>
                    <th className="bg-gray-100 px-4 py-2 font-semibold text-gray-700">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {isFetching && (
                    <tr>
                      <td colSpan={3} className="relative px-4 py-6 text-center text-gray-500">
                        <LoadingOverlay visible={isFetching} loaderProps={{ type: "dots" }} />
                      </td>
                    </tr>
                  )}

                  {!isFetching && isError && (
                    <tr>
                      <td colSpan={3} className="px-4 py-2 text-center text-red-500">
                        <p>
                          Erro ao carregar os caminhões. <br /> Tente novamente mais tarde.
                        </p>
                      </td>
                    </tr>
                  )}

                  {!isFetching && !isError && (
                    <>
                      {truck?.length > 0 &&
                        truck.map((truck, key) => {
                          const licensePlate = truck.licensePlate;

                          return (
                            <tr key={key} className="border-b hover:bg-gray-50">
                              <td className="px-4 py-2 text-center font-mono">{licensePlate.replace("-", "")}</td>
                              <td className="px-4 py-2 text-center">{truck.renavam}</td>
                              <td className="flex items-center justify-center gap-2 px-4 py-2">
                                <ActionIcon.Group>
                                  <Tooltip label="Editar caminhão" color="blue" withArrow>
                                    <ActionIcon color="blue" onClick={() => handleNavigateToEditTruck(truck.id)} radius="sm">
                                      <PencilIcon />
                                    </ActionIcon>
                                  </Tooltip>

                                  <Tooltip label="Excluir caminhão" color="red" withArrow>
                                    <ActionIcon color="red" onClick={() => handleUnbindTruck(truck.id)} radius="sm">
                                      <TrashIcon />
                                    </ActionIcon>
                                  </Tooltip>
                                </ActionIcon.Group>
                              </td>
                            </tr>
                          );
                        })}

                      <tr>
                        <td colSpan={3} className="px-4 py-2 text-center text-gray-500">
                          <div className="flex flex-col items-center justify-center">
                            {!truck?.length && <p>Nenhum caminhão vinculado.</p>}

                            <Tooltip label="Adicionar caminhão" color="blue" withArrow>
                              <ActionIcon onClick={() => navigate({ to: "/app/caminhao/novo" })} color="blue" variant="light">
                                <PlusIcon weight="bold" />
                              </ActionIcon>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Controller
            name="contacts.active"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Checkbox.Group
                label="Métodos de contato"
                description="Selecione os métodos de contato que deseja autorizar"
                className="rounded-md bg-gray-300/25 p-2 shadow-sm"
                error={error?.message}
                {...field}
              >
                <Group mt={"xs"}>
                  <Checkbox label="Email" value="email" />
                  <Checkbox label="SMS" value="sms" />
                  <Checkbox label="WhatsApp" value="whats" />
                </Group>
              </Checkbox.Group>
            )}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Button type="submit" color="green" leftSection={<CheckFatIcon weight="fill" />} loading={isSubmitting} disabled={!isValid} fullWidth>
            Salvar
          </Button>

          <Button color="red" leftSection={<TrashIcon />} onClick={handleConfirmDelete} disabled={!isValid || isSubmitting} fullWidth>
            Deletar Conta
          </Button>
        </div>
      </Card>
    </form>
  );
}
